import { useCallback, useMemo } from 'react';
import { getXtreamServerUrl, useAuth } from '../contexts/AuthContext';
import { useDevice } from '../contexts/DeviceContext';
import { useParentalControls } from '../contexts/ParentalControlsContext';

export type ParentalContentType = 'live' | 'movies' | 'series';

export function useParentalContentLocks() {
  const { authData } = useAuth();
  const { isDemo } = useDevice();
  const {
    state,
    isEnabled,
    isBlocked,
    setBlockedLiveCategories,
    setBlockedMovieCategories,
    setBlockedSeriesCategories,
    setLiveChannelBlocked: setStoredLiveChannelBlocked,
  } = useParentalControls();

  const scope = useMemo(() => {
    if (isDemo) return 'demo';
    if (!authData) return 'unknown';
    return authData.profileId || `${getXtreamServerUrl(authData.server)}|${authData.user.username}`;
  }, [authData, isDemo]);
  const prefix = `${scope}::`;
  const categoryKey = useCallback(
    (type: ParentalContentType, id: string) => `${prefix}${type === 'live' ? '' : `${type}::`}category::${id}`,
    [prefix]
  );
  const channelKey = useCallback((id: string) => `${prefix}channel::${id}`, [prefix]);

  const categoryIds = useCallback((type: ParentalContentType) => {
    if (type === 'movies') return state.blockedMovieCategoryIds;
    if (type === 'series') return state.blockedSeriesCategoryIds;
    return state.blockedLiveCategoryIds;
  }, [state.blockedLiveCategoryIds, state.blockedMovieCategoryIds, state.blockedSeriesCategoryIds]);

  const isCategoryBlocked = useCallback(
    (type: ParentalContentType, id?: string) => Boolean(id && categoryIds(type).includes(categoryKey(type, id))),
    [categoryIds, categoryKey]
  );
  const isLiveChannelBlocked = useCallback(
    (id?: string) => Boolean(id && state.blockedLiveChannelIds.includes(channelKey(id))),
    [channelKey, state.blockedLiveChannelIds]
  );
  const isContentRestricted = useCallback(
    (type: ParentalContentType, id?: string, categoryId?: string) => (
      isBlocked(type === 'live' ? 'liveTV' : type) ||
      (isEnabled && (isCategoryBlocked(type, categoryId) || (type === 'live' && isLiveChannelBlocked(id))))
    ),
    [isBlocked, isCategoryBlocked, isEnabled, isLiveChannelBlocked]
  );

  const setBlockedCategories = useCallback((type: ParentalContentType, ids: string[]) => {
    const current = categoryIds(type);
    const next = [
      ...current.filter((id) => !id.startsWith(prefix)),
      ...ids.map((id) => categoryKey(type, id)),
    ];
    if (type === 'movies') setBlockedMovieCategories(next);
    else if (type === 'series') setBlockedSeriesCategories(next);
    else setBlockedLiveCategories(next);
  }, [categoryIds, categoryKey, prefix, setBlockedLiveCategories, setBlockedMovieCategories, setBlockedSeriesCategories]);

  const setLiveChannelBlocked = useCallback(
    (id: string, blocked: boolean) => setStoredLiveChannelBlocked(channelKey(id), blocked),
    [channelKey, setStoredLiveChannelBlocked]
  );

  return {
    isEnabled,
    isCategoryBlocked,
    isLiveChannelBlocked,
    isContentRestricted,
    setBlockedCategories,
    setLiveChannelBlocked,
  };
}
